// src/pages/dashboard/Payments.tsx
import { Download, CreditCard, CheckCircle } from 'lucide-react';
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next'; 
import { Seo } from '../../components/Seo';

const TRANSACTIONS = [
  { id: '#PAY-001', description: 'Pro Plan — Monthly Subscription', amount: 249, status: 'paid', date: 'Jun 1, 2024', method: 'Visa •••• 4242' },
  { id: '#PAY-002', description: 'Pro Plan — Monthly Subscription', amount: 249, status: 'paid', date: 'May 1, 2024', method: 'Visa •••• 4242' },
  { id: '#PAY-003', description: 'Listing Boost — Mercedes C-Class', amount: 99, status: 'paid', date: 'Apr 15, 2024', method: 'Visa •••• 4242' },
  { id: '#PAY-004', description: 'Basic Plan — Monthly Subscription', amount: 99, status: 'paid', date: 'Apr 1, 2024', method: 'Mastercard •••• 8888' },
];

function PaymentsSkeleton() {
  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton className="h-8 w-40 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-28 rounded-xl" />
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-5">
        <Skeleton className="h-6 w-40 mb-5" />
        <div className="flex items-center justify-between p-4 bg-[#F5F7FA] rounded-xl">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-7 rounded-md" />
            <div>
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <Skeleton className="h-6 w-16 rounded-lg" />
        </div>
        <Skeleton className="h-4 w-36 mt-4" />
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="p-5 border-b border-[#E5E7EB]">
          <Skeleton className="h-6 w-44" />
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {[1, 2, 3, 4, 5].map((row) => (
            <div key={row} className="flex items-center gap-4 px-5 py-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-4 w-56 mb-2" />
                <Skeleton className="h-3 w-36" />
              </div>
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-7 w-20 rounded-lg" />
              <Skeleton className="w-9 h-9 rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

export function Payments() {
  const { t } = useTranslation('auth'); 
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Get status translation
  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      paid: t('payments.status.paid'),
      failed: t('payments.status.failed'),
      pending: t('payments.status.pending'),
      refunded: t('payments.status.refunded'),
    };
    return map[status] || status;
  };

  if (loading) {
    return <PaymentsSkeleton />;
  }

  return (
    <>
    <Seo
      title="Payments & Transactions | Morrocars"
      description="Manage your payment methods and view your transaction history, payment status and billing details on Morrocars."
    />
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl text-[#2D323A]">{t('payments.title')}</h1>
          <p className="text-[#717182]">{t('payments.subtitle')}</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] bg-white rounded-xl hover:border-[#1E6DFF] text-sm text-[#2D323A] transition-colors">
          <Download className="w-4 h-4" /> {t('payments.export')}
        </button>
      </div>

      {/* Payment Methods */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-5">
        <h3 className="text-[#2D323A] mb-4">{t('payments.paymentMethods.title')}</h3>
        <div className="flex items-center justify-between p-4 bg-[#F5F7FA] rounded-xl mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-7 bg-[#1A1F71] rounded flex items-center justify-center">
              <span className="text-white text-xs">VISA</span>
            </div>
            <div>
              <p className="text-sm text-[#2D323A]">Visa •••• 4242</p>
              <p className="text-xs text-[#717182]">Expires 08/26</p>
            </div>
          </div>
          <span className="text-xs px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] rounded-lg">{t('payments.paymentMethods.default')}</span>
        </div>
        <button className="text-sm text-[#1E6DFF] hover:underline">{t('payments.paymentMethods.add')}</button>
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="p-5 border-b border-[#E5E7EB]">
          <h3 className="text-[#2D323A]">{t('payments.transactionHistory')}</h3>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {TRANSACTIONS.map((t, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-4">
              <div className="w-10 h-10 bg-[#22C55E]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-[#22C55E]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#2D323A] truncate">{t.description}</p>
                <p className="text-xs text-[#717182]">{t.method} · {t.date}</p>
              </div>
              <span className="text-sm text-[#2D323A]">{t.amount} MAD</span>
              <span className={`text-xs px-2 py-0.5 rounded-lg flex items-center gap-1 ${t.status === 'paid' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>
                <CheckCircle className="w-3 h-3" /> {getStatusLabel(t.status)}
              </span>
              <button className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182]">
                <Download className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}
